import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';

import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';
import {IStore} from 'redux/interface';
import {getBackpackContent, backpackAsyncActions, getTranslations, getFavoriteNewItems} from 'redux/pageAsyncActions';
import {removeFavoriteCardAction} from 'redux/content/contentAsyncActions';
import {IArticleCard} from 'redux/content/interfaces';

import {ReactComponent as BackpackImg} from 'assets/image/backpack/backpack.svg';
import CrossroadImg from 'assets/image/library/skill-crossroad.svg';

import CardSection from 'components/card-section';
import Loader from 'components/loader';
import NotFoundInfo from 'components/not-found-info';
import {getLocalizedTitle} from 'components/header/utils';
import {FavoritesTabs, FavoritesTab} from 'components/favorites-tabs';
import {FavoriteCardMenu} from 'components/favorite-card-menu';

import './styles.scss';

const PINNED_FAVORITES_STORAGE_KEY = 'pinnedFavorites';

function loadPinnedIds(): number[] {
  try {
    const raw = localStorage.getItem(PINNED_FAVORITES_STORAGE_KEY);

    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function savePinnedIds(ids: number[]): void {
  try {
    localStorage.setItem(PINNED_FAVORITES_STORAGE_KEY, JSON.stringify(ids));
  } catch (e) {
    // localStorage may be unavailable (private browsing, quota) - pinning just won't persist
  }
}

export const Backpack: React.FunctionComponent = () => {
  const {
    backpackContent, favoriteNewItems, content, resourceContent
  } = useSelector((state: IStore) => state.content);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    settings
  } = useSelector((state: IStore) => state.auth);
  const {
    locale
  } = useSelector((state: IStore) => state.translation);
  const dispatch = useDispatch();
  const history = useNavigate();
  const intl = useIntl();
  const [isLoadCards, setIsLoadCards] = useState(true);
  const [activeTab, setActiveTab] = useState<FavoritesTab>('all');
  const [pinnedIds, setPinnedIds] = useState<number[]>(() => loadPinnedIds());
  const [removedIds, setRemovedIds] = useState<number[]>([]);

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Backpack.toLowerCase());

    return page?.translationKey || TranslationKeys.Backpack;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Backpack.toLowerCase());

    return page?.title || Pages.Backpack;
  }, [settings]);

  const localizedTitle = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Backpack.toLowerCase());

    return getLocalizedTitle(page?.title, page?.titles, locale);
  }, [settings, locale]);

  useEffect(() => {
    dispatch(backpackAsyncActions[getBackpackContent]());
    dispatch(backpackAsyncActions[getFavoriteNewItems]());
    setIsLoadCards(false);
  }, []);

  // Removing a favorite only flips `favorite` to false in place (see redux/content/reducer.ts) -
  // it never drops the item from backpackContent - so "removed" items are tracked locally here
  // instead of relying on `favorite`, which the favorites-list endpoint doesn't reliably set on
  // every item (everything it returns is already a favorite by definition).
  const visibleFavorites = useMemo(
    () => backpackContent.filter((item) => !removedIds.includes(item.id)),
    [backpackContent, removedIds]
  );

  // The favorites API returns Library and Resource items in one flat list with no field
  // identifying which type each is. Bucketing is best-effort: it matches against whatever
  // Library/Resource items are already loaded in this session, so an item never browsed
  // elsewhere this session won't appear under its type tab (it still appears under "All").
  const libraryIds = useMemo(() => new Set(content.map((item) => item.id)), [content]);
  const resourceIds = useMemo(() => new Set(resourceContent.map((item) => item.id)), [resourceContent]);

  const tabFilteredFavorites = useMemo(() => {
    if (activeTab === 'library') {
      return visibleFavorites.filter((item) => libraryIds.has(item.id));
    }

    if (activeTab === 'resource') {
      return visibleFavorites.filter((item) => resourceIds.has(item.id));
    }

    return visibleFavorites;
  }, [activeTab, visibleFavorites, libraryIds, resourceIds]);

  const sortedFavorites = useMemo(() => {
    return [...tabFilteredFavorites].sort((a, b) => {
      const aPinned = pinnedIds.includes(a.id);
      const bPinned = pinnedIds.includes(b.id);

      if (aPinned === bPinned) {
        return 0;
      }

      return aPinned ? -1 : 1;
    });
  }, [tabFilteredFavorites, pinnedIds]);

  function handleOpenCard(cardId: number) {
    history(`/content#${cardId}`);
  }

  function togglePin(cardId: number) {
    setPinnedIds((prev) => {
      const next = prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId];

      savePinnedIds(next);

      return next;
    });
  }

  function handleRemove(cardId: number) {
    setRemovedIds((prev) => [...prev, cardId]);
    dispatch(removeFavoriteCardAction(cardId));
  }

  function renderCardMenu(card: IArticleCard) {
    return (
      <FavoriteCardMenu
        pinned={pinnedIds.includes(card.id)}
        onTogglePin={() => togglePin(card.id)}
        onRemove={() => handleRemove(card.id)}
      />
    );
  }

  function renderFavorites() {
    if (visibleFavorites.length === 0) {
      return (
        <NotFoundInfo
          title={intl.formatMessage({
            id: 'gritx.backpack.noFavorites',
            defaultMessage: 'No Favorites Yet'
          })}
          description={intl.formatMessage({
            id: 'gritx.backpack.noFavorites.help',
            defaultMessage: 'Tap the heart icon when you view the article to add your favorites here'
          })}
          image={CrossroadImg}
        />
      );
    }

    return (
      <>
        <FavoritesTabs active={activeTab} onChange={setActiveTab}/>
        {sortedFavorites.length === 0 ? (
          <NotFoundInfo
            title={intl.formatMessage({
              id: 'gritx.backpack.noFavorites.tab',
              defaultMessage: 'No Favorites in This Category Yet'
            })}
            image={CrossroadImg}
          />
        ) : (
          <CardSection
            cards={sortedFavorites}
            onClickCard={handleOpenCard}
            newItems={favoriteNewItems || []}
            cardMenu={renderCardMenu}
          />
        )}
      </>
    );
  }

  return <div className={'backpack container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="backpack__title">
          {/* <BackpackImg className="backpack__icon"/>*/}
          {localizedTitle || (
            <FormattedMessage
              id={`gritx.common.navigation.${title}`}
              defaultMessage={title}
            />
          )}
        </h1>
        <div className="backpack__cards">
          {
            loading.includes(getBackpackContent) || isLoadCards ? <Loader nested/> : renderFavorites()}
        </div>
      </>
    }

  </div>;
};
